import { type InvocationDebugRecord } from "@/shared/domain/admin/ChatDebugRecord";

/** Token breakdown for one recorded Bedrock call, ready for display. */
export interface TurnTokenSummary {
  /** True prompt size Bedrock processed = uncached input + cache reads + cache
   *  writes. */
  sentTokens: number;
  /** Tokens served from the prompt cache (billed at a fraction of input). */
  cacheReadTokens: number;
  /** Tokens written to the prompt cache this call (billed at a premium). */
  cacheWriteTokens: number;
  /** The uncompressed-equivalent size, when compression/truncation shaved off a
   *  meaningful amount; otherwise null. */
  uncompressedTokens: number | null;
  /** Percent the sent prompt is smaller than its uncompressed equivalent, or
   *  null when there was nothing to compare. */
  savedPct: number | null;
}

/**
 * Summarize one invocation's token usage for the debug header.
 *
 * The load-bearing fix: the prompt size Bedrock saw is
 * ``input_tokens + cache_read_tokens + cache_write_tokens``, NOT
 * ``input_tokens`` alone. With prompt caching on, ``input_tokens`` is only the
 * *uncached remainder* — it can be ~1 even over a 100k-token context, because
 * the rest was read from or written to the cache. Treating that 1 as "sent"
 * made a fully-cached turn read as a 100%-compression win. The
 * compression-saved percentage therefore compares the true sent size (not the
 * uncached sliver) against the uncompressed equivalent.
 *
 * Returns null when no invocation was recorded (legacy turns / capture failure)
 * — the caller falls back to its local estimate.
 */
export function summarizeTurnTokens(
  invocation: InvocationDebugRecord | undefined,
): TurnTokenSummary | null {
  const input = invocation?.input_tokens ?? null;
  if (input === null) {
    return null;
  }
  const cacheReadTokens = invocation?.cache_read_tokens ?? 0;
  const cacheWriteTokens = invocation?.cache_write_tokens ?? 0;
  const sentTokens = input + cacheReadTokens + cacheWriteTokens;

  const original = invocation?.estimated_original_input_tokens ?? null;
  const uncompressedTokens =
    original !== null && original > sentTokens ? original : null;
  const savedPct =
    uncompressedTokens !== null
      ? Math.round(
          ((uncompressedTokens - sentTokens) / uncompressedTokens) * 100,
        )
      : null;

  return {
    sentTokens,
    cacheReadTokens,
    cacheWriteTokens,
    uncompressedTokens,
    savedPct,
  };
}
